/**
 * Hooks for standalone API sequence CRUD and execution.
 *
 * @module FlowDesigner/api/hooks/useApiSequence
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../../../config';
import { toApiError } from '../../../../../lib/api-error';
import type { ApiSequenceResult } from '@chatsdet/types';
import { saveApiSequenceRequest, updateApiSequenceConfigRequest } from './apiSequenceRequests';
import type {
  ApiSequenceDto,
  ApiSequenceRunDto,
  SaveApiSequenceInput,
  UpdateApiSequenceConfigInput,
} from './apiSequenceRequests';

// ── Types ──────────────────────────────────────────────────────────────────

export type { ApiSequenceDto, ApiSequenceRunDto } from './apiSequenceRequests';

// ── Query: Fetch API sequence for a workflow ───────────────────────────────

export function useApiSequence(workflowId: string | null) {
  return useQuery({
    queryKey: ['api-sequence', workflowId],
    queryFn: async () => {
      if (!workflowId) return null;
      const r = await fetch(`${API_URL}/api-sequences/by-workflow/${workflowId}`, {
        credentials: 'include',
      });
      if (r.status === 404) return null;
      if (!r.ok) throw new Error('Failed to fetch API sequence');
      const data = await r.json();
      return data.data as ApiSequenceDto;
    },
    enabled: !!workflowId,
    staleTime: 30_000,
  });
}

// ── Query: List runs for an API sequence ───────────────────────────────────

export function useApiSequenceRuns(
  apiSequenceId: string | null,
  opts: { page: number; pageSize: number } = { page: 1, pageSize: 20 }
) {
  return useQuery({
    queryKey: ['api-sequence-runs', apiSequenceId, opts.page, opts.pageSize],
    queryFn: async () => {
      if (!apiSequenceId)
        return { data: [], pagination: { page: 1, pageSize: 20, totalItems: 0, totalPages: 0 } };
      const params = new URLSearchParams({
        page: String(opts.page),
        pageSize: String(opts.pageSize),
      });
      const r = await fetch(`${API_URL}/api-sequences/${apiSequenceId}/runs?${params}`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error('Failed to fetch runs');
      return r.json() as Promise<{
        ok: boolean;
        data: ApiSequenceRunDto[];
        pagination: { page: number; pageSize: number; totalItems: number; totalPages: number };
      }>;
    },
    enabled: !!apiSequenceId,
    staleTime: 10_000,
  });
}

// ── Mutation: Execute API sequence ─────────────────────────────────────────

export function useApiSequenceExecute(apiSequenceId: string | null, workflowId?: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!apiSequenceId) throw new Error('No API sequence ID');
      const r = await fetch(`${API_URL}/api-sequences/${apiSequenceId}/execute`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
      });
      if (!r.ok) throw await toApiError(r, 'API sequence execution failed');
      return r.json() as Promise<{
        ok: boolean;
        data: { runId: string; result: ApiSequenceResult };
      }>;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['api-sequence-runs', apiSequenceId] });
      // Refresh workflow detail so the approve CTA and derived eligibility can
      // surface once the backend records the flow's first passing run.
      if (workflowId) {
        void queryClient.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
      }
    },
  });
}

// ── Mutation: Save API sequence ────────────────────────────────────────────

export function useApiSequenceSave() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SaveApiSequenceInput) => saveApiSequenceRequest(data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['api-sequence', variables.workflowId] });
    },
  });
}

// ── Mutation: Delete API sequence ──────────────────────────────────────────

export function useApiSequenceDelete(workflowId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (apiSequenceId: string) => {
      const r = await fetch(`${API_URL}/api-sequences/${apiSequenceId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw new Error('Failed to delete API sequence');
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['api-sequence', workflowId] });
    },
  });
}

// ── Mutation: Update API sequence config ─────────────────────────────────

export function useApiSequenceUpdateConfig(workflowId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateApiSequenceConfigInput) => updateApiSequenceConfigRequest(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['api-sequence', workflowId] });
    },
  });
}

// ── Mutation: Toggle apiOnly on a workflow ──────────────────────────────────

export function useApiOnlyToggle(workflowId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (apiOnly: boolean) => {
      if (!workflowId) throw new Error('No workflowId');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/api-only`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ apiOnly }),
      });
      if (!r.ok) throw new Error('Failed to toggle API-only mode');
      return r.json() as Promise<{ ok: boolean; data: { apiOnly: boolean } }>;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
    },
  });
}
